export type PolicyRiskLevel = "none" | "warning" | "blocked";

export interface PolicySafetyResult {
  riskLevel: PolicyRiskLevel;
  category:
    | "none"
    | "spam"
    | "harassment"
    | "doxxing"
    | "stalking"
    | "phishing"
    | "scam"
    | "illegal_discrimination"
    | "fake_job"
    | "weapons"
    | "drugs"
    | "trafficking"
    | "adult_exploitation"
    | "privacy_abuse"
    | "violence"
    | "other_illegal";
  matchedTerms: string[];
  userMessage: string;
  shouldBlock: boolean;
}

// Map keywords/patterns to categories and severity
interface SafetyRule {
  terms: string[];
  category: PolicySafetyResult["category"];
  level: PolicyRiskLevel;
}

const SAFETY_RULES: SafetyRule[] = [
  {
    terms: [
      "find home address",
      "find someone home address",
      "get personal phone number",
      "get someones home address",
      "get someones personal phone number",
      "stalk someone",
      "stalk her",
      "stalk him",
      "doxx",
      "dox someone",
      "spy on",
      "private records lookup",
      "confidential background"
    ],
    category: "stalking",
    level: "blocked"
  },
  {
    terms: [
      "only hire men",
      "only hire women",
      "young only",
      "avoid hiring disabled",
      "no immigrants",
      "no pregnant",
      "no disabled",
      "no blacks",
      "no whites",
      "no mexicans",
      "no jews",
      "no muslims",
      "must be straight",
      "only young people",
      "under 30 only",
      "exclude older",
      "exclude disabled",
      "men only",
      "women only",
      "no minorities"
    ],
    category: "illegal_discrimination",
    level: "blocked"
  },
  {
    terms: [
      "pretend to be their bank",
      "pretend to be support",
      "impersonate bank",
      "fake invoice",
      "wire transfer scam",
      "phishing campaign",
      "steal credentials",
      "gift card transfer",
      "bank login fake",
      "phish them"
    ],
    category: "phishing",
    level: "blocked"
  },
  {
    terms: [
      "spam blast",
      "spam campaign",
      "deceptive cold email",
      "bulk spam",
      "mass scraper",
      "harvest email addresses",
      "unsolicited blast"
    ],
    category: "spam",
    level: "blocked"
  },
  {
    terms: [
      "trafficking",
      "sex worker recruitment",
      "adult exploitation",
      "prostitution hiring",
      "smuggle human"
    ],
    category: "trafficking",
    level: "blocked"
  },
  {
    terms: [
      "buy cocaine",
      "sell cocaine",
      "buy heroin",
      "illegal drugs",
      "buy weapons",
      "unlicensed firearm",
      "buy bomb",
      "contraband distribution"
    ],
    category: "drugs",
    level: "blocked"
  },
  {
    terms: [
      "beat up",
      "harass employee",
      "threaten worker",
      "harass them",
      "intimidate",
      "scare them"
    ],
    category: "harassment",
    level: "blocked"
  },
  {
    // Warnings for borderline/potential outreach risks
    terms: [
      "cold outreach blast",
      "scraping contacts",
      "bulk campaign",
      "unsolicited email",
      "outreach list",
      "scraper"
    ],
    category: "spam",
    level: "warning"
  },
  {
    terms: [
      "background check details",
      "personal info lookup",
      "find where someone lives"
    ],
    category: "privacy_abuse",
    level: "warning"
  }
];

export function detectProhibitedUse(input: string): PolicySafetyResult {
  if (!input) {
    return {
      riskLevel: "none",
      category: "none",
      matchedTerms: [],
      userMessage: "",
      shouldBlock: false
    };
  }

  const clean = input.toLowerCase().trim();

  // Find rules triggered by the input
  const triggeredBlocked: { rule: SafetyRule; terms: string[] }[] = [];
  const triggeredWarning: { rule: SafetyRule; terms: string[] }[] = [];

  for (const rule of SAFETY_RULES) {
    const matched = rule.terms.filter(term => clean.includes(term));
    if (matched.length > 0) {
      if (rule.level === "blocked") {
        triggeredBlocked.push({ rule, terms: matched });
      } else {
        triggeredWarning.push({ rule, terms: matched });
      }
    }
  }

  // Handle blocked cases first
  if (triggeredBlocked.length > 0) {
    const highestRisk = triggeredBlocked[0];
    return {
      riskLevel: "blocked",
      category: highestRisk.rule.category,
      matchedTerms: highestRisk.terms,
      userMessage: "BizNearYou cannot help with illegal, abusive, deceptive, discriminatory, or harmful activity. Please revise your search or action to use the platform for lawful, professional purposes.",
      shouldBlock: true
    };
  }

  // Handle warning cases next
  if (triggeredWarning.length > 0) {
    const warning = triggeredWarning[0];
    return {
      riskLevel: "warning",
      category: warning.rule.category,
      matchedTerms: warning.terms,
      userMessage: "Use BizNearYou responsibly. Do not use contact data, job posts, or outreach tools for spam, harassment, deception, discrimination, or illegal activity.",
      shouldBlock: false
    };
  }

  return {
    riskLevel: "none",
    category: "none",
    matchedTerms: [],
    userMessage: "",
    shouldBlock: false
  };
}
